import React, { ReactNode, useState } from 'react';
import { Area, AreaChart, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Card, Statistic, Spin, Space } from 'antd';
import { CustomTooltip } from './CustomTooltip';

interface MetricTotal {
  label: string;
  value: number;
  color: string;
  type: string;
}

export interface DataPoint {
  date: string;
  count: number;
  type: string;
}

interface LineChartProps {
  title: string;
  icon: ReactNode;
  data: DataPoint[];
  metrics: MetricTotal[];
  xField: string;
  yField: string;
  width?: string | number;
  height?: number;
  isLoading?: boolean;
  bare?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({
  title,
  icon,
  data,
  metrics,
  xField,
  yField,
  width = '100%',
  height = 400,
  isLoading = false,
  bare = false,
}) => {
  const [activeMetrics, setActiveMetrics] = useState<string[]>(metrics.map(m => m.type));

  const toggleMetric = (type: string) => {
    setActiveMetrics(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Transform data for Recharts
  const transformedData = data.reduce((acc: any[], curr) => {
    const existingDate = acc.find(item => item.date === curr.date);
    if (existingDate) {
      existingDate[curr.type] = curr.count;
    } else {
      acc.push({
        date: curr.date,
        [curr.type]: curr.count,
      });
    }
    return acc;
  }, []);

  const filteredMetrics = activeMetrics.length === 0 ? metrics : metrics.filter(m => activeMetrics.includes(m.type));

  // Calculate dynamic tick interval to prevent crowding on x-axis
  const tickInterval = Math.max(0, Math.ceil(transformedData.length / 10) - 1);

  // Smart date formatter based on data range
  const formatDate = (value: string) => {
    const dataLength = transformedData.length;
    if (dataLength > 90) {
      // For ranges > 90 days, show just month (MM)
      return value.slice(5, 7);
    }
    // Default: show MM-DD
    return value.slice(5);
  };

  const content = (
    <div style={{ width }}>
      {isLoading ? (
        <div style={{ 
          height,
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Space
            direction="horizontal"
            wrap
            size={[8, 8]}
            style={{ 
              width: '100%',
              justifyContent: 'flex-start',
              marginBottom: 'clamp(8px, 2vw, 16px)'
            }}
          >
            {metrics.map((metric) => (
                <div
                  key={metric.type}
                  role={metrics.length > 1 ? 'button' : undefined}
                  tabIndex={metrics.length > 1 ? 0 : undefined}
                  onClick={() => metrics.length > 1 && toggleMetric(metric.type)}
                  onKeyDown={(event) => {
                    if (metrics.length > 1 && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      toggleMetric(metric.type);
                    }
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: '132px',
                    minHeight: '72px',
                    border: activeMetrics.includes(metric.type) ? `2px solid ${metric.color}` : 'none',
                    borderRadius: '10px',
                    padding: '8px 12px',
                    background: activeMetrics.includes(metric.type) ? `${metric.color}12` : 'transparent',
                    color: activeMetrics.includes(metric.type) ? metric.color : '#595959',
                    cursor: metrics.length > 1 ? 'pointer' : 'default',
                    opacity: metrics.length > 1 ? 1 : 0.8,
                    transition: 'all 0.2s ease',
                    flex: '1 1 132px',
                    outlineOffset: 2,
                  }}
                >
                  <Statistic
                    title={metric.label}
                    value={metric.value}
                    valueStyle={{
                      color: metric.color,
                      cursor: metrics.length > 1 ? 'pointer' : 'default',
                      fontSize: '18px',
                      lineHeight: '1.1',
                      marginBottom: '2px',
                    }}
                    style={{
                      fontSize: '12px',
                      lineHeight: '1.1',
                    }}
                  />
                </div>
            ))}
          </Space>
          <div style={{ 
            height,
            marginTop: 8,
            overflowX: 'auto',
            overflowY: 'hidden',
            // minWidth: '300px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ 
              minWidth: '400px', 
              height: '100%',
              position: 'relative',
              zIndex: 1
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={transformedData}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 10,
                    bottom: 10,
                  }}
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    marginBottom: '30px',
                  }}
                >
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    interval={tickInterval}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={formatDate}
                    tick={{ fontSize: 'clamp(8px, 1.2vw, 10px)' }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 'clamp(8px, 1.2vw, 10px)' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {filteredMetrics.map((metric, index) => (
                    <Area
                      key={metric.type}
                      type="monotone"
                      dataKey={metric.type}
                      stroke={metric.color}
                      fill='transparent'
                      fillOpacity={0.3}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (bare) return content;

  return (
    <Card title={title} style={{ width }}>
      {content}
    </Card>
  );
};
